import { getDateFormatted, isValidTimeFormat } from '@vindr/utils/helpers/common.helper';
import useToggle from '@vindr/utils/hooks/useToggle';
import { Checkbox, DatePicker, Form, TimePicker } from 'antd';
import dayjs from 'dayjs';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import handleArrowKeyNavigation from '../../handleArrowKeyNavigation';
import { StructuredReportElement } from '../types';
import DateTimeElement from './DateTimeElement';
import useRefreshComponent from '@vindr/utils/hooks/useRefreshComponent';

export interface DateTimeProps {
  disabled: boolean;
  element: DateTimeElement;
  onChange: (val: string | number, element: StructuredReportElement) => void;
  index?: number;
}

const DateTime = ({ disabled, element, onChange, index }: DateTimeProps) => {
  const [default_date, default_time] = [element?.default_date, element?.default_time];
  const { value: open, setFalse: setClose, toggle } = useToggle();
  const { t } = useTranslation('Vindoc');
  const [isHidden, setIsHidden] = useState(element.is_hidden);
  const [refresh, setRefresh] = useRefreshComponent();

  useEffect(() => {
    element.setIsHidden(isHidden);
    onChange(element.value, element);
    setRefresh();
  }, [element, isHidden, onChange, setRefresh]);

  const Picker =
    default_date && default_time
      ? DatePicker
      : default_time && !default_date
      ? TimePicker
      : DatePicker;

  const placeholder = useMemo(() => {
    if (element.placeholder) return t(element.placeholder);
    if (default_date && default_time) return t('Select a date and time');
    if (default_time && !default_date) return t('Select a time');
    if (!default_time && default_date) return t('Select a date');
  }, [default_date, default_time, element.placeholder, t]);

  return (
    <Fragment key={refresh}>
      <div key={element.id} className="flex items-start">
        <Checkbox
          checked={!isHidden}
          className="mr-2 mt-0.5"
          onChange={(e) => setIsHidden(!e.target.checked)}
        />
        <Form.Item
          key={element.id}
          className="mb-3 flex-1"
          label={[element?.name, index ?? ''].join(' ')}
          tooltip={{ title: element.tooltip }}
        >
          <Picker
            className="w-full"
            defaultValue={element.value ? dayjs(element.value) : null}
            disabled={disabled}
            format={element.format || getDateFormatted(true)}
            open={open}
            placeholder={placeholder}
            showTime={isValidTimeFormat(element.format)}
            onBlur={setClose}
            onChange={(date) => onChange(date ? date.format() : '', element)}
            onClick={toggle}
            onKeyDown={(event) => handleArrowKeyNavigation({ event, toggle, open: true })}
            onOk={setClose}
            onOpenChange={(open: boolean) => {
              if (!open) setClose();
            }}
          />
        </Form.Item>
      </div>
    </Fragment>
  );
};

export default DateTime;
